import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StaffNavbar from '../components/StaffNavbar';
import BackButton from '../components/BackButton';
import apiService from '../services/api';
import '../styles/StaffPages.css';

const TASK_FIELDS = [
  {
    key: 'profile',
    label: 'Create account / profile',
    description: 'Points awarded when a user finishes creating their account.',
  },
  {
    key: 'link_card',
    label: 'Activate linked card',
    description: 'Points awarded the first time a user links an eligible card.',
  },
  {
    key: 'safari_extension',
    label: 'Activate Safari extension',
    description: 'Points awarded the first time Safari spending is activated.',
  },
];

const inputStyle = {
  padding: '10px 12px',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  fontSize: '14px',
  fontFamily: '"Baikal Book"',
  width: '140px',
};

const buttonStyle = {
  border: 'none',
  borderRadius: '8px',
  padding: '10px 16px',
  fontFamily: '"Baikal Book"',
  fontWeight: 700,
  cursor: 'pointer',
};

const SuperuserSetupTaskRewards = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [form, setForm] = useState({
    profile: 15,
    link_card: 25,
    safari_extension: 45,
  });

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    const load = async () => {
      try {
        const currentUser = await apiService.fetchCurrentUser();
        if (!currentUser?.is_superuser) {
          navigate('/staff/dashboard/');
          return;
        }

        const data = await apiService.getSetupTaskRewardsSuperuser();
        setForm({
          profile: Number(data.profile) || 0,
          link_card: Number(data.link_card) || 0,
          safari_extension: Number(data.safari_extension) || 0,
        });
      } catch (err) {
        console.error('Failed to load setup task rewards:', err);
        setError(err.response?.data?.error || 'Failed to load setup task rewards');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [navigate]);

  const handleChange = (key, value) => {
    const next = value === '' ? '' : Math.max(0, parseInt(value, 10) || 0);
    setForm(prev => ({ ...prev, [key]: next }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const payload = {
        profile: Number(form.profile) || 0,
        link_card: Number(form.link_card) || 0,
        safari_extension: Number(form.safari_extension) || 0,
      };
      const data = await apiService.updateSetupTaskRewardsSuperuser(payload);
      setForm({
        profile: Number(data.profile) || 0,
        link_card: Number(data.link_card) || 0,
        safari_extension: Number(data.safari_extension) || 0,
      });
      showToast('Setup task points saved');
    } catch (err) {
      console.error('Failed to save setup task rewards:', err);
      setError(err.response?.data?.error || 'Failed to save setup task rewards');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <StaffNavbar activeSection="superuser" />
        <div className="admin-main">
          <div className="admin-content">
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
              Loading...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection="superuser" />
      <div className="admin-main">
        <div className="admin-content">
          <BackButton text="Back to Superuser Dashboard" to="/superuser/dashboard/" />
          <h1 className="staff-page-header">Account Setup Points</h1>
          <p className="staff-page-subheader">
            Set how many points the mobile app awards for each unlock setup task.
          </p>

          {toast && (
            <div
              style={{
                marginTop: '16px',
                padding: '12px 16px',
                borderRadius: '8px',
                background: toast.type === 'success' ? '#ecfdf5' : '#fef2f2',
                color: toast.type === 'success' ? '#065f46' : '#991b1b',
              }}
            >
              {toast.message}
            </div>
          )}

          {error && (
            <div
              style={{
                marginTop: '16px',
                padding: '12px 16px',
                borderRadius: '8px',
                background: '#fef2f2',
                color: '#991b1b',
              }}
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ marginTop: '32px', maxWidth: '640px' }}>
            {TASK_FIELDS.map(field => (
              <div
                key={field.key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '24px',
                  padding: '20px 0',
                  borderBottom: '1px solid #e5e7eb',
                }}
              >
                <div>
                  <div style={{ fontFamily: '"Baikal Book"', fontWeight: 700, marginBottom: '4px' }}>
                    {field.label}
                  </div>
                  <div style={{ color: '#6b7280', fontSize: '14px' }}>{field.description}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={form[field.key]}
                    onChange={e => handleChange(field.key, e.target.value)}
                    style={inputStyle}
                  />
                  <span style={{ color: '#6b7280', fontSize: '14px' }}>pts</span>
                </div>
              </div>
            ))}

            <div style={{ marginTop: '28px' }}>
              <button
                type="submit"
                disabled={saving}
                style={{
                  ...buttonStyle,
                  background: saving ? '#9ca3af' : '#00C8FF',
                  color: '#000',
                  opacity: saving ? 0.7 : 1,
                }}
              >
                {saving ? 'Saving...' : 'Save points'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SuperuserSetupTaskRewards;
